import { AnswerInput, AnswerView } from '../../services/service-evaluation-answer';
import {
  EvaluationCommentRule,
  EvaluationLimits,
  EvaluationQuestionType,
} from '../../util/evaluationContract';

export type AnswerQuestion = AnswerView['questions'][number];
export type DraftAnswer = { score: number | null; skipped: boolean; comment: string; text: string };
export type Draft = Record<string, DraftAnswer>;

export type DraftCheck = {
  requiredTotal: number;
  requiredDone: number;
  answeredCount: number;
  missingComments: string[];
  tooLong: string[];
  canSubmit: boolean;
};

export const EMPTY_ANSWER: DraftAnswer = { score: null, skipped: false, comment: '', text: '' };

export const answerOf = (draft: Draft, key: string): DraftAnswer => draft[key] ?? EMPTY_ANSWER;

const isBlank = (value: string) => !value.trim();

export const isAnswerable = (question: AnswerQuestion): boolean =>
  question.type !== EvaluationQuestionType.Paragraph;

export const isAnswered = (question: AnswerQuestion, answer: DraftAnswer): boolean =>
  question.type === EvaluationQuestionType.Text
    ? !isBlank(answer.text)
    : answer.score !== null || answer.skipped;

export const needsComment = (question: AnswerQuestion, answer: DraftAnswer): boolean =>
  question.type === EvaluationQuestionType.Rating &&
  answer.score !== null &&
  (question.commentRule === EvaluationCommentRule.Always ||
    (question.commentRule === EvaluationCommentRule.Below3 &&
      answer.score < EvaluationLimits.CommentRequiredBelow));

export const isMissingComment = (question: AnswerQuestion, answer: DraftAnswer): boolean =>
  needsComment(question, answer) && isBlank(answer.comment);

export const isTooLong = (answer: DraftAnswer): boolean =>
  answer.comment.length > EvaluationLimits.CommentMaxLength ||
  answer.text.length > EvaluationLimits.TextAnswerMaxLength;

export const checkDraft = (questions: AnswerQuestion[], draft: Draft): DraftCheck => {
  const answerable = questions.filter(isAnswerable);
  const required = answerable.filter(q => q.required);
  const requiredDone = required.filter(q => isAnswered(q, answerOf(draft, q.key))).length;
  const answeredCount = answerable.filter(q => isAnswered(q, answerOf(draft, q.key))).length;
  const missingComments = answerable
    .filter(q => isMissingComment(q, answerOf(draft, q.key)))
    .map(q => q.key);
  const tooLong = answerable.filter(q => isTooLong(answerOf(draft, q.key))).map(q => q.key);

  return {
    requiredTotal: required.length,
    requiredDone,
    answeredCount,
    missingComments,
    tooLong,
    canSubmit:
      requiredDone === required.length &&
      answeredCount > 0 &&
      !missingComments.length &&
      !tooLong.length,
  };
};

export const toAnswerInputs = (questions: AnswerQuestion[], draft: Draft): AnswerInput[] =>
  questions.filter(isAnswerable).flatMap((question): AnswerInput[] => {
    const answer = answerOf(draft, question.key);
    if (question.type === EvaluationQuestionType.Text) {
      return isBlank(answer.text) ? [] : [{ key: question.key, text: answer.text }];
    }
    if (!isAnswered(question, answer) && isBlank(answer.comment)) return [];
    return [
      {
        key: question.key,
        score: answer.skipped ? null : answer.score,
        skipped: answer.skipped,
        comment: answer.comment,
      },
    ];
  });
